import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/shared/components/markdown/Markdown';
import { getErrorMessage } from '@/core/api/response-handler';
import { useAuthProviderV2 } from '../../../../providers/provider-hooks';
import { useLessonQuery, useLessonsQuery } from '../../hooks/useLessonsQueries';
import { ButonDescarcaExercitii } from '../../components/ButonDescarcaExercitii';
import {
  CONFIG_DEPUNERE,
  infoSlidesForDetails,
  practicalPaths,
  provocareDejaDepusa,
  rezumatProvocare,
  stareProvocareText,
  termenExpirat,
  type TipDepunere,
} from './practical-evidence';
import { PracticalPageShell } from './PracticalPageShell';

/**
 * Pagina e lata (`latime="lata"`, 25.09): imaginile iau tot cardul (1024px), textul ramane pe o
 * coloana de citire centrata, altfel randurile ajung la ~150 de caractere pe desktop. In `rem`, nu
 * `max-w-prose`: `65ch` depinde de font, deci titlul (font mare) si paragraful ieseau de latimi
 * diferite si nealiniate (masurat la 1440: h1 942px, paragraf 520px). Bara de jos sta pe aceeasi coloana.
 */
const COLOANA_TEXT = 'mx-auto w-full max-w-3xl';

/**
 * B3: pagina „Detalii" a unei activitati practice — descrierea si slide-urile INFO, pe un singur
 * scroll, fara parcursul de lectie. Profesorul are jos bara „Încarcă poze"; ADMIN/DBEST doar citesc.
 */
export default function PracticalDetailsPage({ tip = 'PRACTICAL_ACTIVITY' }: { tip?: TipDepunere }) {
  const cfg = CONFIG_DEPUNERE[tip];
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userInfo } = useAuthProviderV2();
  const { data: lesson, isLoading, isError, error, refetch, isFetching } = useLessonQuery(id);
  const sectiuni = useMemo(() => infoSlidesForDetails(lesson?.slides), [lesson?.slides]);

  const eProfesor = userInfo?.role === 'PROFESOR';
  const expirat = termenExpirat(lesson?.deadline);
  // Provocarea pentru scoala: starea depunerii vine din LISTA (contoare pe scoala), nu din GET-ul
  // lectiei. Cat lista nu s-a incarcat, bara ramane vizibila; serverul refuza oricum a doua depunere.
  const lista = useLessonsQuery(tip, undefined, { enabled: cfg.oSinguraDepunere && eProfesor });
  const dinLista = cfg.oSinguraDepunere ? lista.data?.find((l) => l.id === id) : undefined;
  const dejaDepusa = provocareDejaDepusa(dinLista?.depuneriInAsteptare, dinLista?.depuneriAprobate);
  const stareProvocare = stareProvocareText(dinLista?.depuneriInAsteptare, dinLista?.depuneriAprobate);
  const rezumat = cfg.oSinguraDepunere ? rezumatProvocare(lesson?.punctajMaxim, lesson?.deadline) : null;
  // Final review M2: backendul nu filtreaza lectiile ascunse la GET /api/lessons/{id}. Profesorul
  // nu vede o activitate ascunsa nici pe lista, deci nici prin link direct; ADMIN/DBEST o vad.
  const ascunsaPentruMine = eProfesor && !!lesson?.isHidden;

  let continut;
  if (isLoading) {
    continut = (
      <div className="space-y-4" aria-busy="true" aria-label={cfg.texte.seIncarca}>
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="aspect-video w-full rounded-lg" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  } else if (isError) {
    continut = (
      <div role="alert" className="space-y-4">
        <p className="text-base text-pretty text-destructive">
          {getErrorMessage(error, cfg.texte.neincarcata)}
        </p>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          Încearcă din nou
        </Button>
      </div>
    );
  } else if (!lesson || lesson.type !== tip || ascunsaPentruMine) {
    continut = <p className="text-base text-pretty">{cfg.texte.negasita}</p>;
  } else {
    const text = COLOANA_TEXT;
    continut = (
      <article className="space-y-6 text-base leading-relaxed">
        {/* `leading-tight`: altfel h1 mosteneste `leading-relaxed` al articolului, iar un titlu pe
            doua randuri se desparte vizibil in doua bucati (audit Task 12, capturile la 360/1440). */}
        <h1 className={`${text} text-fluid-2xl font-bold leading-tight text-balance`}>{lesson.title}</h1>
        {rezumat && (
          <p data-provocare-rezumat className={`${text} font-semibold text-primary`}>
            {rezumat}
          </p>
        )}
        {expirat && (
          <p role="status" className={`${text} font-medium text-warning`}>
            {cfg.texte.termenExpirat}
          </p>
        )}
        {stareProvocare && (
          <p role="status" data-provocare-stare className={`${text} font-medium`}>
            {stareProvocare}
          </p>
        )}
        {lesson.image && (
          /* Coperta e deasupra pliului: fara lazy, cu prioritate mare (LCP). */
          <img
            src={lesson.image}
            alt={lesson.title}
            width={1200}
            height={675}
            /* Minuscule: React 18 nu recunoaste `fetchPriority` (avertisment in dev) si il scrie
               oricum ca atribut HTML; forma minuscula e atributul real, fara avertisment. */
            {...{ fetchpriority: 'high' }}
            className="aspect-video w-full rounded-lg object-cover"
          />
        )}
        <Markdown source={lesson.description} className={text} />

        {sectiuni.map((s) => (
          <section key={s.key} className="space-y-3">
            {s.title && <h2 className={`${text} text-fluid-xl font-semibold text-balance`}>{s.title}</h2>}
            {s.image && (
              <img
                src={s.image}
                alt={s.title || lesson.title}
                width={1200}
                height={675}
                loading="lazy"
                className="aspect-video w-full rounded-lg object-contain"
              />
            )}
            <Markdown source={s.content} className={text} />
          </section>
        ))}

        {lesson.pdfManualUrl && (
          // Task 15: descarcare reala prin backend (vezi ButonDescarcaExercitii).
          <div className={`${text} space-y-2`}>
            <ButonDescarcaExercitii
              lessonId={lesson.id ?? id}
              titlu={lesson.title ?? ''}
              pdfManualUrl={lesson.pdfManualUrl}
              variant="outline"
              className="w-full sm:w-auto"
            />
          </div>
        )}
      </article>
    );
  }

  // Bara nu apare pe o activitate expirata: un buton dezactivat, lipit de ecran la fiecare derulare,
  // repeta degeaba mesajul de sub titlu; iar un buton activ ar duce la un formular care nu poate reusi.
  // La fel dupa ce scoala a trimis deja dovezile unei provocari: a doua depunere ar fi refuzata.
  const eActivitate = !!lesson && lesson.type === tip && !ascunsaPentruMine && !expirat && !dejaDepusa;
  const arataBara = eProfesor;

  return (
    <PracticalPageShell
      latime="lata"
      lista={cfg.lista}
      footer={
        arataBara &&
        eActivitate && (
          <div className="sticky bottom-0 border-t border-border bg-background/95 px-4 pt-3 safe-area-bottom">
            <div className={COLOANA_TEXT}>
              <Button className="w-full" onClick={() => navigate(practicalPaths(id, tip).incarca)}>
                <Upload aria-hidden="true" />
                {cfg.texte.butonIncarca}
              </Button>
            </div>
          </div>
        )
      }
    >
      {continut}
    </PracticalPageShell>
  );
}
